import {
  Clock,
  Vector3,
  SphereGeometry,
  ShaderMaterial,
  Mesh,
  Color,
  DoubleSide,
  AdditiveBlending,
  Vector3
} from "three";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";

import { bodies } from "../configEntities/celestialDynamicsConfig.js";
import fragmentAround from "../../../shaders/distortionHole/fragmentWhite.glsl";
import vertexAround from "../../../shaders/distortionHole/vertexWhite.glsl";

import { normalizeAlphabetic } from "../helpers.js";
import { cameraLabelClick } from "../utils/cameraUtils.js";
import { showInfoPanel } from "../ui.js";
import { sceneManager} from "../script";
import { INCLIN_GALAXY } from "../configEntities/galaxyConfig.js";

export class WhiteHole {
  constructor(size, label) {
    this.scene = sceneManager.scene;
    this.camera = sceneManager.camera;
    const distSargitarius = 26640 * 63241.1 * 23.4117;

    const z = distSargitarius * Math.cos(INCLIN_GALAXY),
          y = -distSargitarius * Math.sin(INCLIN_GALAXY),
          posHoleBlack = new Vector3(0, y, z),
          
          n = new Vector3(0, Math.sin(INCLIN_GALAXY), Math.cos(INCLIN_GALAXY)),
          d = n.dot(posHoleBlack),
          posHoleWhite = posHoleBlack.clone().sub(n.multiplyScalar(1.5 * d));

    this.pos = posHoleWhite;
    this.distSpace = -1
    this.scaleSpace = 1
    this.size = size;
    this.name = label;
    this.distScale =
      bodies[this.name.toLowerCase().replace(/\s+/g, "")].distScale;
    this.speedDisk = 3e-10;

    this.objOsc = bodies[this.name.toLowerCase().replace(/\s+/g, "")];

    if (this.objOsc.magnetic) {
      const m = this.objOsc.magnetic;
      this.inclinMag = m.inclinMag;
      this.totalPhi = m.totalPhi;
      this.totalRings = m.totalRings;
      this.desiredMaxRmax = m.desiredMaxRmax;
      this.desiredMinRmax = m.desiredMinRmax;
      this.lateralTwist = m.lateralTwist;
      this.twistFrequency = m.twistFrequency;
      this.amortMag = m.amortMag;
      this.distMag = m.distMag;
    }

    this.axialTilt = 0;
    this.speedRot = 1.2e-2;
    this.distRot = this.objOsc.distRot;
    this.clock = new Clock();

    this.createMesh();
  }

  _initSeeds() {
    this.seeds = [];
    this.speeds = [];
    for (let i = 0; i < 10; i++) {
      this.seeds.push(Math.random());
      this.speeds.push(0.1 + Math.random() * 0.3);
    }
  }

  createMesh() {
    this._initSeeds();
    this.matAround = new ShaderMaterial({
      transparent: true,
      uniforms: {
        color1: { value: new Color(0xacdff0) },
        color2: { value: new Color(0xffffff) },
        uTime: { value: 0.0 },
        seeds: { value: this.seeds },
        speeds: { value: this.speeds },
        startRadius: { value: 0 },
      },
      vertexShader: vertexAround,
      fragmentShader: fragmentAround,
      blending: AdditiveBlending,
      depthTest: true,
      depthWrite: false,
      side: DoubleSide,
    });

  
    this.geoAround = new SphereGeometry(this.size*0.75, 64, 64);
    this.mesh = new Mesh(this.geoAround, this.matAround);
    this.mesh.position.copy(this.pos);
    this.scene.add(this.mesh);

    let labeNorm = normalizeAlphabetic(this.name);
    this.div = document.createElement("div");
    this.div.className = "label";
    this.div.textContent = this.name;
    this.div.style.backgroundColor = "transparent";
    this.div.style.cursor = "pointer";
    this.infoIcon = document.createElement("i");
    this.infoIcon.className = "fas fa-info-circle info-icon";
    this.infoIcon.style.pointerEvents = "auto";
    this.infoIcon.title = "More Information";
    this.div.appendChild(this.infoIcon);
    this.div.addEventListener("click", () => {
      cameraLabelClick(labeNorm, this);
    });

    this.infoIcon.addEventListener("click", (evt) => {
      evt.stopPropagation();
      showInfoPanel(this.name);
    });
    const canvas = sceneManager.renderer.domElement;
    this.div.addEventListener('wheel', e => {
      e.preventDefault();        
      const evt = new WheelEvent('wheel', e);
      canvas.dispatchEvent(evt);    
    });

    this.label = new CSS2DObject(this.div);
    this.label.position.set(0, this.size * 1.1, 0);
    this.label.center.set(0, 1);
    this.mesh.add(this.label);
    this.label.layers.set(0);
  }

  handleLabelClick() {
    sceneManager.commands.controls.camTarget = this.name.toLowerCase().replace(/\s+/g, "");
    sceneManager.commands.controls.mode = "Automatic";
    helpersUpdateTween(this.camera, this);
  }

  applyGrav(grav, camera) {
    const d = camera.position.distanceTo(this.pos);
    if (d < 1.3e7) {
      const f = Math.min(10, grav / d);
      const dir = new Vector3().copy(camera.position).sub(this.pos).normalize();
      camera.position.add(dir.multiplyScalar(f));
    }
  }

  animate(speed) {


    this.label.visible = sceneManager.commands.controls.labels;
    this.matAround.uniforms.uTime.value += Math.min(Math.abs(speed), 1e-2);
    this.applyGrav(2, this.camera);
  }
}
